import { useState, useEffect, useRef, useMemo } from "react";
import { TrenitaliaLogo } from "../../../shared/components/TrenitaliaLogo";
import { TypingIndicator } from "./TypingIndicator";
import { DocumentDialog } from "./DocumentDialog";
import { useRelativeTime } from "../hooks/useRelativeTime";
import "./Message.css";

const MessageTimestamp = ({ createdAt, isTyping }) => {
  const label = useRelativeTime(createdAt);
  // While the assistant is generating, show the thinking placeholder
  // instead of a relative time (there is no meaningful "ago" yet).
  return (
    <span className="message-time" title={new Date(createdAt).toLocaleString("it-IT")}>
      {isTyping ? "Sto pensando..." : label}
    </span>
  );
};

export const Message = ({
  message,
  onFeedback,
  feedbackSent,
  onTableResize,
}) => {
  const [feedback, setFeedback] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const messageContentRef = useRef(null);

  // Funzione per creare una mappa degli ID ai documenti
  const createDocumentMap = (fonti) => {
    const documentMap = new Map();

    fonti.forEach((fonte) => {
      const original = fonte.documento_originale;

      if (
        fonte.fonte_indice === "evento-provvedimenti" &&
        original?.id_evento
      ) {
        documentMap.set(`E-${original.id_evento}`, fonte);
      } else if (fonte.fonte_indice === "messaggi" && original?.id_messaggio) {
        documentMap.set(`M-${original.id_messaggio}`, fonte);
      } else if (
        fonte.fonte_indice === "provvedimenti-regionali" &&
        original?.id_provvedimento
      ) {
        documentMap.set(`ER-${original.id_provvedimento}`, fonte);
      } else if (fonte.fonte_indice === "vco-emails" &&
        original?.id_email) {
        documentMap.set(`VCO-${original.id_email}`, fonte);
      }
      else if (fonte.fonte_indice==="infomobilita-feed-rss" && original?.id_infomobilita )
      {
          documentMap.set(`RSS-${original.id_infomobilita}`, fonte);
      }
    });

    return documentMap;
  };

  // Mappa dei documenti per i riferimenti
  const documentMap = createDocumentMap(message.fonti || []);

  // Event delegation per i riferimenti cliccabili
  useEffect(() => {
    const messageContent = messageContentRef.current;
    if (!messageContent) return;

    const handleReferenceClick = (event) => {
      // Verifica se l'elemento cliccato è un reference-link
      if (event.target.classList.contains("reference-link")) {
        event.preventDefault();

        // Estrai il riferimento dal testo del bottone
        const referenceText = event.target.textContent;
        let reference = referenceText;

        // Se il riferimento è tra parentesi, rimuovile
        if (referenceText.startsWith("(") && referenceText.endsWith(")")) {
          reference = referenceText.slice(1, -1);
        }

        const documento = documentMap.get(reference);
        if (documento) {
          handleDocumentClick(documento);
        }
      }
    };

    messageContent.addEventListener("click", handleReferenceClick);

    return () => {
      messageContent.removeEventListener("click", handleReferenceClick);
    };
  }, [documentMap, message.content]);

  const handleFeedback = (isLike) => {
    setFeedback(isLike ? "liked" : "disliked");
    onFeedback?.(message.id, isLike);
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setShowDocumentDialog(true);
  };

  const formatContent = (content) => {
    // Cache per tabelle già parsate (per performance)
    const tableCache = new Map();

    // Funzione per convertire markdown in JSX
    const parseMarkdown = (text) => {
      // Gestisce le tabelle markdown con parsing ottimizzato
      const parseTable = (tableText) => {
        // Controlla cache prima
        if (tableCache.has(tableText)) {
          return tableCache.get(tableText);
        }

        const lines = tableText.trim().split("\n");
        if (lines.length < 2) return tableText;

        // Validazione rapida
        const separatorLine = lines[1];
        if (!separatorLine.includes("-")) return tableText;

        // Headers parsing ottimizzato
        const headers = lines[0]
          .split("|")
          .map((h) => h.trim())
          .filter((h) => h);
        if (headers.length === 0) return tableText;

        // Data rows parsing ottimizzato
        const dataRows = [];
        for (let i = 2; i < lines.length; i++) {
          const row = lines[i]
            .split("|")
            .map((cell) => cell.trim())
            .filter((cell) => cell);
          if (row.length > 0) dataRows.push(row);
        }

        // Calcolo larghezza semplificato (solo durante il rendering completo)
        if (onTableResize && dataRows.length > 0) {
          const estimatedWidth = Math.min(headers.length * 150 + 200, 1200);
          setTimeout(() => onTableResize(Math.max(estimatedWidth, 650)), 0);
        }

        // Template string più veloce con gestione riferimenti nelle celle
        const headerHtml = headers.map((h) => `<th>${h}</th>`).join("");
        const rowsHtml = dataRows
          .map(
            (row) =>
              `<tr>${row
                .slice(0, headers.length)
                .map((cell) => {
                  // Gestisci i riferimenti nelle celle della tabella
                  let cellContent = cell;

                  const markdownReferencePattern = /\[((?:ER|VCO|E|M|RSS)-[^\]]+)\]\(\1\)/g;

                  cellContent = cellContent.replace(
                    markdownReferencePattern,
                    (match, reference) => {
                      const documento = documentMap.get(reference);
                      if (documento) {
                        return `<button class="reference-link" data-reference="${reference}">${reference}</button>`;
                      }
                      return `<span class="reference-link">${reference}</span>`;
                    }
                  );

                  // Poi gestisci i riferimenti semplici (E-123) e (M-123)
                  const referencePattern = /\((E-[^)]+|M-[^)]+|ER-[^)]+|VCO-[^)]+|RSS-[^)]+)\)/g;
                  cellContent = cellContent.replace(
                    referencePattern,
                    (match, reference) => {
                      const documento = documentMap.get(reference);
                      if (documento) {
                        return `<button class="reference-link" data-reference="${reference}">(${reference})</button>`;
                      }
                      return match;
                    }
                  );

                  return `<td title="${cell}">${cellContent}</td>`;
                })
                .join("")}</tr>`
          )
          .join("");

        const result = `<div class="table-container"><table class="markdown-table">
          <thead><tr>${headerHtml}</tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table></div>`;

        // Salva in cache
        tableCache.set(tableText, result);
        return result;
      };

      // Prima gestiamo le tabelle (prima delle altre trasformazioni)
      text = text.replace(
        /(?:^|\n)(\|[^\n]+\|\n\|[-:\s|]+\|\n(?:\|[^\n]+\|\n?)*)/gm,
        (match, table) => {
          return parseTable(table);
        }
      );

      // Gestisce i riferimenti nel testo normale - sia (E-123) che [E-123](E-123) markdown
      const referencePattern = /\((E-[^)]+|M-[^)]+)\)/g;
      const markdownReferencePattern = /\[([EM]-[^\]]+)\]\(\1\)/g;

      // Prima gestisci i link markdown [E-123](E-123) e [M-123](M-123)
      text = text.replace(markdownReferencePattern, (match, reference) => {
        const documento = documentMap.get(reference);
        if (documento) {
          return `<button class="reference-link" data-reference="${reference}">${reference}</button>`;
        }
        return `<span class="reference-link">${reference}</span>`;
      });

      // Poi gestisci i riferimenti semplici (E-123) e (M-123)
      text = text.replace(referencePattern, (match, reference) => {
        const documento = documentMap.get(reference);
        if (documento) {
          return `<button class="reference-link" data-reference="${reference}">(${reference})</button>`;
        }
        return match;
      });

      // Gestisce i titoli (# ## ###)
      text = text.replace(/^### (.*$)/gim, "<h3>$1</h3>");
      text = text.replace(/^## (.*$)/gim, "<h2>$1</h2>");
      text = text.replace(/^# (.*$)/gim, "<h1>$1</h1>");

      // Gestisce il testo in grassetto (**testo**)
      text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

      // Gestisce il testo in corsivo (*testo*)
      text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");

      // Gestisce i link [testo](url)
      text = text.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      );

      // Gestisce il codice inline (`codice`)
      text = text.replace(/`([^`]+)`/g, "<code>$1</code>");

      // Gestisce le liste puntate (- item)
      text = text.replace(/^[\s]*-\s+(.*)$/gim, "<li>$1</li>");
      text = text.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

      // Gestisce le liste numerate (1. item)
      text = text.replace(/^[\s]*\d+\.\s+(.*)$/gim, "<li>$1</li>");

      // Gestisce le interruzioni di riga
      text = text.replace(/\n/g, "<br/>");

      // Evidenzia "Range temporale" in grassetto e aggiungi 3 <br> sopra e sotto
      text = text.replace(
        /(Range temporale[^:]*:\s*)(\d{4}-\d{2}-\d{2}(?:\s*\d{2}:\d{2})?\s*→\s*\d{4}-\d{2}-\d{2}(?:\s*\d{2}:\d{2})?\.)/gi,
        "<br><br><br><strong>$1</strong>$2<br><br><br>"
      );

      return text;
    };

    if (content.includes("Riferimenti:")) {
      const [mainText, references] = content.split("Riferimenti:");
      const refList = references
        .trim()
        .split("\n")
        .filter((line) => line.trim());

      return (
        <div>
          <div
            dangerouslySetInnerHTML={{ __html: parseMarkdown(mainText.trim()) }}
          />
          <div className="reference-title">Riferimenti:</div>
          <ol className="numbered-list">
            {refList.map((ref, idx) => (
              <li key={idx}>
                <button
                  className="reference-link"
                  onClick={() => console.log("Clicked reference:", ref)}
                >
                  {ref.replace(/^\d+\.\s*/, "")}
                </button>
              </li>
            ))}
          </ol>
        </div>
      );
    }

    // Applica il parsing markdown anche al contenuto normale
    return <div dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }} />;
  };

  // Memorizza il contenuto parsato per evitare re-rendering costanti
  const formattedContent = useMemo(() => {
    if (typeof message.content === "string") {
      return formatContent(message.content);
    }
    return message.content;
  }, [message.content, documentMap]);

  return (
    <div className={`message ${message.type}`}>
      <div className={`message-header ${message.type}`}>
        {message.type === "assistant" && (
          <div className="message-avatar assistant">
            <TrenitaliaLogo size={40} className="message-avatar" />
          </div>
        )}
        <MessageTimestamp
          createdAt={message.createdAt}
          isTyping={message.isTyping}
        />
        {message.type === "user" && (
          <div className="message-avatar user">U</div>
        )}
      </div>

      {message.isTyping ? (
        <TypingIndicator />
      ) : (
        <div className="message-content" ref={messageContentRef}>
          {formattedContent}
        </div>
      )}

      {/* Citazioni */}
      {message.type === "assistant" &&
        !message.isTyping &&
        message.fonti &&
        message.fonti.length > 0 && (
          <div className="message-sources">
            <div className="sources-header">📚 Fonti:</div>
            <div className="sources-list">
              {message.fonti.map((fonte, index) => {
                const getSourceName = (fonte) => {
                  const original = fonte.documento_originale;

                  // Per le fonti di tipo "messaggi", usa il formato "messaggio-data"
                  if (fonte.fonte_indice === "messaggi" && original?.data) {
                    const data = original.data.split("T")[0]; // Prende solo la parte YYYY-MM-DD
                    return `messaggio-${data}`;
                  }

                  if (fonte.fonte_indice === "vco-emails" && original?.data) {
                    const data = original.data.split("T")[0];
                    return `Email-VCO-${data}`;
                  }

                  if(fonte.fonte_indice==="infomobilita-feed-rss"  && original?.data)
                  {
                      const data = original.data.split("T")[0];
                      return `Evento-Infomobilità-${data}`;
                  }

                  // Per altri tipi, usa il nome documento o oggetto
                  return (
                    original?.nome_documento ||
                    original?.oggetto + ".xlsx" ||
                    `Documento ${index + 1}`
                  );
                };

                return (
                  <button
                    key={index}
                    className="source-citation"
                    onClick={() => handleDocumentClick(fonte)}
                    title={`Punteggio: ${fonte.punteggio_ricerca?.toFixed(
                      2
                    )} - Clicca per dettagli`}
                  >
                    [{index + 1}] {getSourceName(fonte)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {message.type === "assistant" && !message.isTyping && (
        <div className="message-actions">
          <button
            className={`message-action-btn ${
              feedback === "liked" ? "liked" : ""
            }`}
            onClick={() => handleFeedback(true)}
            disabled={feedbackSent}
          >
            👍
          </button>
          <button
            className={`message-action-btn ${
              feedback === "disliked" ? "disliked" : ""
            }`}
            onClick={() => handleFeedback(false)}
            disabled={feedbackSent}
          >
            👎
          </button>
        </div>
      )}

      <DocumentDialog
        document={selectedDocument}
        isOpen={showDocumentDialog}
        onClose={() => setShowDocumentDialog(false)}
      />
    </div>
  );
};
